import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { useI18n } from "@/context/I18nContext";
import ForcePasswordChange from "@/pages/ForcePasswordChange";

const AuthContext = createContext(null);
export const useAuth = () => useContext(AuthContext);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const i18n = useI18n();

  const checkAuth = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
      if (data?.language) i18n?.applyUserLang(data.language);
    } catch {
      setUser(false);
    } finally {
      setLoading(false);
    }
  }, [i18n]);

  useEffect(() => {
    if (window.location.hash?.includes("session_id=")) {
      setLoading(false);
      return;
    }
    checkAuth();
  }, [checkAuth]);

  const login = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    if (data.token) {
      localStorage.setItem("kewag_token", data.token);
      setUser(data.user);
      if (data.user?.language) i18n?.applyUserLang(data.user.language);
    }
    return data;
  };

  const complete2fa = async (path, pending_token, code) => {
    const { data } = await api.post(path, { pending_token, code });
    if (data.token) {
      localStorage.setItem("kewag_token", data.token);
      setUser(data.user);
      if (data.user?.language) i18n?.applyUserLang(data.user.language);
    }
    return data;
  };

  const recover2fa = async (email, password) => {
    const { data } = await api.post("/auth/2fa/recover", { email, password });
    return data;
  };

  const forgotPassword = async (email) => {
    const { data } = await api.post("/auth/forgot-password", { email });
    return data;
  };

  const resetPassword = async (token, new_password) => {
    const { data } = await api.post("/auth/reset-password", { token, new_password });
    return data;
  };

  const changePassword = async (current_password, new_password) => {
    const { data } = await api.post("/auth/change-password", { current_password, new_password });
    if (data.token) localStorage.setItem("kewag_token", data.token);
    if (data.user) setUser(data.user);
    return data;
  };

  const logout = async () => {
    const isStaff = user && (user.role === "super_admin" || user.role === "team_member");
    try { await api.post("/auth/logout"); } catch {}
    localStorage.removeItem("kewag_token");
    setUser(false);
    window.location.href = isStaff ? "/admin/login" : "/login";
  };

  const mustChange = !!(user && user.must_change_password);

  return (
    <AuthContext.Provider value={{ user, setUser, loading, login, complete2fa, recover2fa, forgotPassword, resetPassword, changePassword, logout, checkAuth }}>
      {mustChange ? <ForcePasswordChange /> : children}
    </AuthContext.Provider>
  );
}
